export const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || 'legacy').toLowerCase();
export const FIREBASE_AUTH_ENABLED = AUTH_MODE === 'firebase';

let firebaseClientPromise;

function firebaseConfig() {
  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  };
  const missing = Object.entries(config)
    .filter(([, value]) => !value)
    .map(([key]) => key);
  if (missing.length) {
    throw new Error(`Firebase Auth is enabled but public config is missing: ${missing.join(', ')}`);
  }
  return config;
}

async function getFirebaseClient() {
  if (!FIREBASE_AUTH_ENABLED) return null;
  if (!firebaseClientPromise) {
    firebaseClientPromise = Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
    ]).then(async ([appModule, authModule]) => {
      const app = appModule.getApps().length
        ? appModule.getApp()
        : appModule.initializeApp(firebaseConfig());
      const auth = authModule.getAuth(app);
      await authModule.setPersistence(auth, authModule.inMemoryPersistence);
      return { auth, authModule };
    });
  }
  return firebaseClientPromise;
}

export async function loginWithFirebaseSession(api, email, password) {
  if (!FIREBASE_AUTH_ENABLED) {
    throw new Error('Firebase Auth is not enabled');
  }

  const csrfResponse = await api.get('/auth/firebase-csrf');
  const csrfToken = csrfResponse.data?.csrf_token;
  if (!csrfToken) throw new Error('Firebase sign-in protection token is missing');

  const identifierResponse = await api.post(
    '/auth/firebase-login-identifier',
    { email },
    { headers: { 'X-CSRF-Token': csrfToken } },
  );
  const loginEmail = identifierResponse.data?.login_email;
  if (!loginEmail) throw new Error('Firebase login identifier could not be resolved');

  const { auth, authModule } = await getFirebaseClient();
  try {
    const credential = await authModule.signInWithEmailAndPassword(auth, loginEmail, password);
    const idToken = await authModule.getIdToken(credential.user, true);
    const sessionResponse = await api.post(
      '/auth/firebase-session',
      { id_token: idToken },
      { headers: { 'X-CSRF-Token': csrfToken } },
    );
    return sessionResponse.data.user;
  } finally {
    await authModule.signOut(auth).catch(() => {});
  }
}

export async function clearFirebaseClientSession() {
  if (!FIREBASE_AUTH_ENABLED || !firebaseClientPromise) return;
  const { auth, authModule } = await firebaseClientPromise;
  await authModule.signOut(auth).catch(() => {});
}
